import { queryOptions, useQuery, type QueryClient } from '@tanstack/react-query';
import { fetchSession } from '@/api/auth';
import type { AdminSession } from '@/api/types';
import { can, type Permission } from './permissions';

export const SESSION_QUERY_KEY = ['session'] as const;

export const sessionQuery = queryOptions({
  queryKey: SESSION_QUERY_KEY,
  queryFn: ({ signal }) => fetchSession(signal),
  staleTime: 60_000,
  // Returning to the tab re-checks the session, so an expired one is noticed before a click fails.
  refetchOnWindowFocus: true,
  retry: false,
});

export function useSession() {
  const query = useQuery(sessionQuery);
  return {
    session: query.data ?? null,
    isPending: query.isPending,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useCan(): (permission: Permission) => boolean {
  const { session } = useSession();
  return (permission) => can(session?.role, permission);
}

/** Replaces the cached session and drops everything cached under the previous one. */
export function setSession(queryClient: QueryClient, session: AdminSession | null): void {
  queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== SESSION_QUERY_KEY[0] });
  queryClient.setQueryData(SESSION_QUERY_KEY, session);
}
